import { each } from '@antv/util';
import { BBox, IGroup, IShape } from '../../../dependents';
import { LabelItem } from '../interface';

const MAX_TIMES = 100;

/** @ignore */
interface Bitmap {
  [key: number]: {
    [key: number]: boolean;
  };
}

/** @ignore */
interface GreedyCfg {
  readonly xGap?: number;
  readonly yGap?: number;
}

/**
 * @ignore
 * Greedy è´ªå©ªç®æ³
 */
class Greedy {
  public readonly xGap: number;
  /** optimizing for text overlapping detection: use a min text height as gap */
  public readonly yGap: number;

  private bitmap: Bitmap = {};

  constructor(cfg: GreedyCfg = {}) {
    const { xGap = 1, yGap = 8 } = cfg;
    this.xGap = xGap;
    this.yGap = yGap;
  }

  public hasGap(bbox: BBox): boolean {
    let hasGap = true;
    const bitmap = this.bitmap;
    const minX = Math.round(bbox.minX);
    const maxX = Math.round(bbox.maxX);
    const minY = Math.round(bbox.minY);
    const maxY = Math.round(bbox.maxY);
    for (let i = minX; i <= maxX; i += 1) {
      if (!bitmap[i]) {
        bitmap[i] = {};
        continue;
      }
      if (i === minX || i === maxX) {
        for (let j = minY; j <= maxY; j++) {
          if (bitmap[i][j]) {
            hasGap = false;
            break;
          }
        }
      } else {
        if (bitmap[i][minY] || bitmap[i][maxY]) {
          hasGap = false;
          break;
        }
      }
    }
    return hasGap;
  }

  public fillGap(bbox: BBox): void {
    const bitmap = this.bitmap;
    const minX = Math.round(bbox.minX);
    const maxX = Math.round(bbox.maxX);
    const minY = Math.round(bbox.minY);
    const maxY = Math.round(bbox.maxY);
    // filling grid
    for (let i = minX; i <= maxX; i += 1) {
      if (!bitmap[i]) {
        bitmap[i] = {};
      }
    }
    for (let i = minX; i <= maxX; i += this.xGap) {
      for (let j = minY; j <= maxY; j += this.yGap) {
        bitmap[i][j] = true;
      }
      bitmap[i][maxY] = true;
    }

    // filling y edges
    if (this.yGap !== 1) {
      for (let i = minY; i <= maxY; i += 1) {
        bitmap[minX][i] = true;
        bitmap[maxX][i] = true;
      }
    }

    // filling x edges
    if (this.xGap !== 1) {
      for (let i = minX; i <= maxX; i += 1) {
        bitmap[i][minY] = true;
        bitmap[i][maxY] = true;
      }
    }
  }

  public destroy(): void {
    this.bitmap = {};
  }
}

function spiralFill(label: IShape, greedy: Greedy, maxTimes: number = MAX_TIMES) {
  const dt = -1;
  const { x, y } = label.attr();
  const bbox = label.getCanvasBBox();
  const maxDelta = Math.sqrt(bbox.width * bbox.width + bbox.height * bbox.height);
  let dxdy;
  let t = -dt;
  let dx = 0;
  let dy = 0;
  const f = (param: number) => {
    const nt = param * 0.1;
    return [nt * Math.cos(nt), nt * Math.sin(nt)];
  };

  if (greedy.hasGap(bbox)) {
    greedy.fillGap(bbox);
    return true;
  }
  let canFill = false;
  let times = 0;
  const accessedCache = {};
  while (Math.min(Math.abs(dx), Math.abs(dy)) < maxDelta && times < maxTimes) {
    dxdy = f((t += dt));
    dx = ~~dxdy[0];
    dy = ~~dxdy[1];
    if ((!dx && !dy) || accessedCache[`${dx}-${dy}`]) {
      continue;
    }
    label.attr({ x: x + dx, y: y + dy });
    if (dx + dy < 0) {
      label.attr('textAlign', 'right');
    }
    times++;
    if (greedy.hasGap(label.getCanvasBBox())) {
      greedy.fillGap(label.getCanvasBBox());
      canFill = true;
      accessedCache[`${dx}-${dy}`] = true;
      break;
    }
  }
  return canFill;
}

/*
 *  æ ¹æ®å¦ä¸è§åå°è¯æ¾ç½®label
 *                5
 *        ------------------
 *        |    1   |   0   |
 *    8   âââââââââ4ââââââââ   7
 *        |    2   |   3   |
 *        ââââââââââââââââââ
 *                 6
 */
function adjustLabelPosition(label: IShape, x: number, y: number, index: number) {
  const { width, height } = label.getCanvasBBox();
  const attrs = {
    x,
    y,
    textAlign: 'center',
  };
  switch (index) {
    case 0:
      attrs.y -= height + 1;
      attrs.x += 1;
      attrs.textAlign = 'left';
      break;
    case 1:
      attrs.y -= height + 1;
      attrs.x -= 1;
      attrs.textAlign = 'right';
      break;
    case 2:
      attrs.y += height + 1;
      attrs.x -= 1;
      attrs.textAlign = 'right';
      break;
    case 3:
      attrs.y += height + 1;
      attrs.x += 1;
      attrs.textAlign = 'left';
      break;
    case 5:
      attrs.y -= height * 2 + 2;
      break;
    case 6:
      attrs.y += height * 2 + 2;
      break;
    case 7:
      attrs.x += width + 1;
      attrs.textAlign = 'left';
      break;
    case 8:
      attrs.x -= width + 1;
      attrs.textAlign = 'right';
      break;
    default:
      break;
  }
  label.attr(attrs);
  return label.getCanvasBBox();
}

/**
 * @ignore
 * label é²é®æ¡å¸å±ï¼å¨ä¸æ¹å label ä½ç½®çæåµä¸å¯¹ç¸äºéå ç label è¿è¡è°æ´ã
 * ä¸åäº 'overlap' ç±»åçå¸å±ï¼è¯¥å¸å±ä¸ä¼å¯¹ label çä½ç½®è¿è¡åç§»è°æ´ã
 * @param labels åä¸å¸å±è°æ´ç label æ°ç»éå
 */
export function fixedOverlap(items: LabelItem[], labels: IGroup[], shapes: IShape[] | IGroup[], region: BBox) {
  const greedy = new Greedy();
  each(labels, (label: IGroup) => {
    const labelShape = label.find((shape) => shape.get('type') === 'text') as IShape;
    if (!spiralFill(labelShape, greedy)) {
      label.remove(true);
    }
  });
  greedy.destroy();
}

/**
 * @ignore
 * label é²é®æ¡å¸å±ï¼ä¸ºäºé²æ­¢ label ä¹é´ç¸äºè¦çåæ¶ä¿è¯å°½å¯è½å¤ ç label å±ç¤ºï¼éè¿å°è¯å° label å**åå¨åç§»**æ¥åé¤æ¾ä¸ä¸ç label
 * @param labels åä¸å¸å±è°æ´ç label æ°ç»éå
 */
export function overlap(items: LabelItem[], labels: IGroup[], shapes: IShape[] | IGroup[], region: BBox) {
  const greedy = new Greedy();
  each(labels, (label: IGroup) => {
    const labelShape = label.find((shape) => shape.get('type') === 'text') as IShape;
    const { x, y } = labelShape.attr();
    let canFill = false;
    for (let i = 0; i <= 8; i++) {
      const bbox = adjustLabelPosition(labelShape, x, y, i);
      if (greedy.hasGap(bbox)) {
        greedy.fillGap(bbox);
        canFill = true;
        break;
      }
    }
    if (!canFill) {
      label.remove(true);
    }
  });

  greedy.destroy();
}
